import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Button from './ui/Button';
import { parseWeekSeries } from '../utils/timeUtils';
import { previewDraftPublish } from '../services/apiService';
import AcademicYearService from '../services/AcademicYearService';

const PublishDraftModal = ({
    isOpen,
    onClose,
    onPublish,
    currentDraftName,
    timePeriods,
    scheduledInstances,
    selectedYearForView
}) => {
    const [publishMode, setPublishMode] = useState('all');
    const [selectedPeriodId, setSelectedPeriodId] = useState(null);
    const [customStartWeek, setCustomStartWeek] = useState('');
    const [customEndWeek, setCustomEndWeek] = useState('');
    const [isPublishing, setIsPublishing] = useState(false);
    const [warnings, setWarnings] = useState([]);
    const [activeTab, setActiveTab] = useState('stats');

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setPublishMode('all');
            setSelectedPeriodId(null);
            setCustomStartWeek('');
            setCustomEndWeek('');
            setWarnings([]);
            setActiveTab('stats');
        }
    }, [isOpen]);

    // Calculate the effective week range based on publish mode
    const effectiveWeekRange = useMemo(() => {
        if (publishMode === 'all') {
            return null;
        }

        if (publishMode === 'period' && selectedPeriodId) {
            const period = timePeriods.find(p => String(p.id) === String(selectedPeriodId));
            if (period && period.start_week && period.end_week) {
                return { startWeek: period.start_week, endWeek: period.end_week };
            }
        }

        if (publishMode === 'custom') {
            const start = parseInt(customStartWeek, 10);
            const end = parseInt(customEndWeek, 10);
            if (!isNaN(start) && !isNaN(end) && start >= 1 && start <= 53 && end >= 1 && end <= 53) {
                return { startWeek: start, endWeek: end };
            }
        }

        return null;
    }, [publishMode, selectedPeriodId, customStartWeek, customEndWeek, timePeriods]);

    // Helper to check if entry's first week is in range
    const isEntryInRange = useCallback((entry, startWeek, endWeek) => {
        if (!entry.weekSeries) return false;
        try {
            const weeks = parseWeekSeries(entry.weekSeries);
            if (weeks.length === 0) return false;
            const firstWeek = Math.min(...weeks);
            return startWeek <= endWeek
                ? firstWeek >= startWeek && firstWeek <= endWeek
                : firstWeek >= startWeek || firstWeek <= endWeek;
        } catch {
            return false;
        }
    }, []);

    // State for server preview data
    const [previewData, setPreviewData] = useState(null);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);

    // Academic year structure (start/end weeks and periods)
    const [yearStructure, setYearStructure] = useState(null);

    // Load academic year structure when modal opens
    useEffect(() => {
        let isMounted = true;
        const loadStructure = async () => {
            if (!isOpen) return;
            try {
                const svc = AcademicYearService.getInstance();
                const structure = await svc.getAcademicYearStructure();
                if (isMounted) {
                    setYearStructure(structure);
                }
            } catch (e) {
                console.warn('Failed to load academic year structure', e);
                if (isMounted) setYearStructure(null);
            }
        };
        loadStructure();
        return () => { isMounted = false; };
    }, [isOpen]);

    // Fetch preview data from server when parameters change
    useEffect(() => {
        const fetchPreview = async () => {
            if (!isOpen || !currentDraftName || !selectedYearForView) {
                setPreviewData(null);
                return;
            }

            setIsLoadingPreview(true);
            try {
                const data = await previewDraftPublish(
                    currentDraftName,
                    selectedYearForView,
                    effectiveWeekRange
                );

                setPreviewData(data);
            } catch (error) {
                console.error('Error fetching preview data:', error);
                setPreviewData(null);
            } finally {
                setIsLoadingPreview(false);
            }
        };

        fetchPreview();
    }, [isOpen, currentDraftName, selectedYearForView, effectiveWeekRange]);

    // Use server preview data for change analysis
    const changeAnalysis = useMemo(() => {
        if (!previewData) {
            return {
                stats: {
                    liveBeforeCount: 0,
                    liveAfterCount: 0,
                    draftEntriesCount: 0,
                    addedCount: 0,
                    removedCount: 0,
                    modifiedCount: 0,
                    unchangedCount: 0
                },
                added: [],
                removed: [],
                modified: [],
                unchanged: []
            };
        }

        return {
            stats: {
                liveBeforeCount: previewData.stats.liveBeforeCount,
                liveAfterCount: previewData.stats.liveAfterCount,
                draftEntriesCount: previewData.stats.draftEntriesCount,
                addedCount: previewData.changes.added.length,
                removedCount: previewData.changes.removed.length,
                modifiedCount: previewData.changes.modified.length,
                unchangedCount: previewData.changes.unchanged.length
            },
            added: previewData.changes.added,
            removed: previewData.changes.removed,
            modified: previewData.changes.modified,
            unchanged: previewData.changes.unchanged
        };
    }, [previewData]);

    useEffect(() => {
        if (!effectiveWeekRange || !scheduledInstances.length) {
            setWarnings([]);
            return;
        }

        const newWarnings = [];
        const { startWeek, endWeek } = effectiveWeekRange;

        const isWeekInRange = (week) => {
            if (startWeek <= endWeek) {
                return week >= startWeek && week <= endWeek;
            } else {
                return week >= startWeek || week <= endWeek;
            }
        };

        const yearEntries = scheduledInstances.filter(
            entry => entry.isScheduled && entry.isScheduled() && entry.year === selectedYearForView
        );

        yearEntries.forEach(entry => {
            if (!entry.weekSeries) return;

            try {
                const weeks = parseWeekSeries(entry.weekSeries);
                if (weeks.length === 0) return;

                const firstWeek = Math.min(...weeks);
                const weeksInRange = weeks.filter(w => isWeekInRange(w));

                if (weeksInRange.length > 0 && !isWeekInRange(firstWeek)) {
                    const entryName = entry.displayName || entry.course || entry.title || `Entry ${entry.id}`;
                    newWarnings.push(
                        `"${entryName}" starts at week ${firstWeek} (outside range) but has ${weeksInRange.length} week(s) in the selected range`
                    );
                }
            } catch (error) {
                console.error('Error parsing week series for entry:', entry.id, error);
            }
        });

        setWarnings(newWarnings);
    }, [effectiveWeekRange, scheduledInstances, selectedYearForView]);

    const handlePublish = async () => {
        setIsPublishing(true);
        try {
            await Promise.resolve(onPublish(effectiveWeekRange));
            onClose();
        } catch (error) {
            console.error('Error publishing draft:', error);
        } finally {
            setIsPublishing(false);
        }
    };

    const isValidSelection = () => {
        if (publishMode === 'all') return true;
        if (publishMode === 'period') return selectedPeriodId !== null;
        if (publishMode === 'custom') {
            const start = parseInt(customStartWeek, 10);
            const end = parseInt(customEndWeek, 10);
            return !isNaN(start) && !isNaN(end) && start >= 1 && start <= 53 && end >= 1 && end <= 53;
        }
        return false;
    };

    const { stats, added, removed, modified } = changeAnalysis;

    // Build ordered week sequence for the academic year (handles wrap-around)
    const weekSequence = useMemo(() => {
        const start = yearStructure?.startWeek;
        const end = yearStructure?.endWeek;
        const createSeq = (s, e) => {
            if (!s || !e) return Array.from({ length: 53 }, (_, i) => i + 1);
            if (s <= e) return Array.from({ length: e - s + 1 }, (_, i) => s + i);
            const first = Array.from({ length: 53 - s + 1 }, (_, i) => s + i);
            const second = Array.from({ length: e }, (_, i) => i + 1);
            return [...first, ...second];
        };
        return createSeq(start, end);
    }, [yearStructure]);

    // Helpers to normalize week series field names
    const getWeekSeriesFromEntry = useCallback((entry) => {
        // Backend DB objects use week_series; change lists use weekSeries
        return entry?.week_series || entry?.weekSeries || '';
    }, []);

    const computeWeekCounts = useCallback((entries) => {
        const counts = new Map();
        if (!entries || entries.length === 0) return counts;
        const validWeeks = new Set(weekSequence);
        for (const entry of entries) {
            const series = getWeekSeriesFromEntry(entry);
            if (!series) continue;
            try {
                const weeks = parseWeekSeries(series);
                for (const w of weeks) {
                    if (validWeeks.has(w)) {
                        counts.set(w, (counts.get(w) || 0) + 1);
                    }
                }
            } catch (e) {
                // ignore invalid series
            }
        }
        return counts;
    }, [getWeekSeriesFromEntry, weekSequence]);

    // Derive entry sets for charts
    const liveKeptEntries = useMemo(() => (previewData?.finalLiveEntries || []).filter(e => e.source === 'existing_live'), [previewData]);
    const liveRemovedEntries = useMemo(() => (previewData?.changes?.removed || []), [previewData]);
    const draftEntriesToPublish = useMemo(() => (previewData?.changes?.added || []), [previewData]);
    const liveAfterEntries = useMemo(() => (previewData?.finalLiveEntries || []), [previewData]);

    // Compose live-before as kept + removed
    const liveBeforeEntries = useMemo(() => ([...liveKeptEntries, ...liveRemovedEntries]), [liveKeptEntries, liveRemovedEntries]);

    // Compute week counts for each scenario
    const liveBeforeCounts = useMemo(() => computeWeekCounts(liveBeforeEntries), [computeWeekCounts, liveBeforeEntries]);
    const draftCounts = useMemo(() => computeWeekCounts(draftEntriesToPublish), [computeWeekCounts, draftEntriesToPublish]);
    const liveAfterCounts = useMemo(() => computeWeekCounts(liveAfterEntries), [computeWeekCounts, liveAfterEntries]);

    // Unified max across Live Before and Draft to keep scales coherent across charts
    const unifiedBarMax = useMemo(() => {
        let max = 0;
        for (const w of weekSequence) {
            const lb = liveBeforeCounts.get(w) || 0;
            const dr = draftCounts.get(w) || 0;
            if (lb > max) max = lb;
            if (dr > max) max = dr;
        }
        return Math.max(max, 1);
    }, [weekSequence, liveBeforeCounts, draftCounts]);

    // Period background helpers
    const periodSegments = useMemo(() => {
        if (!yearStructure?.periods || weekSequence.length === 0) return [];
        const totalWeeks = weekSequence.length;
        const indexOfWeek = (w) => weekSequence.findIndex(x => x === w);
        const segments = [];
        yearStructure.periods.forEach((p, idx) => {
            const s = p.start_week;
            const e = p.end_week;
            if (s == null || e == null) return;
            if (s <= e) {
                const startIdx = indexOfWeek(s);
                const endIdx = indexOfWeek(e);
                if (startIdx !== -1 && endIdx !== -1) segments.push({ colorIndex: idx, start: startIdx, length: endIdx - startIdx + 1, period: p });
            } else {
                const startIdx1 = indexOfWeek(s);
                const endIdx1 = weekSequence.length - 1;
                if (startIdx1 !== -1) segments.push({ colorIndex: idx, start: startIdx1, length: endIdx1 - startIdx1 + 1, period: p });
                const startIdx2 = 0;
                const endIdx2 = indexOfWeek(e);
                if (endIdx2 !== -1) segments.push({ colorIndex: idx, start: startIdx2, length: endIdx2 - startIdx2 + 1, period: p });
            }
        });
        return { totalWeeks, segments };
    }, [yearStructure, weekSequence]);

    const periodColors = ['#E3F2FD', '#E8F5E9', '#FFF3E0', '#F3E5F5', '#E0F2F1', '#FCE4EC'];

    // Range helper for coloring bars
    const isWeekInSelectedRange = useCallback((week) => {
        if (!effectiveWeekRange) return true; // publishing all => entire year highlighted
        const { startWeek, endWeek } = effectiveWeekRange;
        if (startWeek <= endWeek) {
            return week >= startWeek && week <= endWeek;
        }
        return week >= startWeek || week <= endWeek;
    }, [effectiveWeekRange]);

    // Mini chart component
    const MiniScheduleChart = ({ title, counts, colorizeRange = false, barMax }) => {
        const totalWeeks = weekSequence.length || 1;
        return (
            <div className="w-full">
                <div className="text-sm font-medium text-gray-900 mb-2">{title}</div>
                <div className="relative border rounded p-2 bg-white">
                    {/* Period backgrounds */}
                    <div className="absolute inset-0 pointer-events-none" aria-hidden>
                        {periodSegments.segments && periodSegments.segments.map((seg, i) => {
                            const leftPct = (seg.start / periodSegments.totalWeeks) * 100;
                            const widthPct = (seg.length / periodSegments.totalWeeks) * 100;
                            const color = periodColors[seg.colorIndex % periodColors.length];
                            return (
                                <div key={`seg-${i}`} style={{ left: `${leftPct}%`, width: `${widthPct}%`, backgroundColor: color, opacity: 0.35 }} className="absolute top-0 bottom-0" />
                            );
                        })}
                    </div>
                    {/* Bars */}
                    <div className="relative grid gap-[2px]" style={{ gridTemplateColumns: `repeat(${totalWeeks}, minmax(0, 1fr))` }}>
                        {weekSequence.map((w) => {
                            const value = counts.get(w) || 0;
                            const rawPct = (value / barMax) * 100;
                            const minPct = 20; // minimum visible height when there is at least one entry
                            const heightPct = value > 0 ? Math.max(rawPct, minPct) : 0;
                            const inRange = colorizeRange && isWeekInSelectedRange(w) && value > 0;
                            return (
                                <div key={`w-${w}`} className="flex items-end justify-center min-h-[64px]">
                                    <div className={`w-full ${inRange ? 'bg-green-600' : 'bg-blue-600'}`} style={{ height: `${heightPct}%`, opacity: 0.8 }} title={`Week ${w}: ${value} course${value === 1 ? '' : 's'}`} />
                                </div>
                            );
                        })}
                    </div>
                    {/* X-axis labels (sparse) */}
                    <div className="mt-1 grid text-[10px] text-gray-500" style={{ gridTemplateColumns: `repeat(${totalWeeks}, minmax(0, 1fr))` }}>
                        {weekSequence.map((w, idx) => (
                            <div key={`l-${w}`} className="text-center">
                                {idx % 4 === 0 ? `W${w}` : ''}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const ChartsBlock = () => (
        <div className="space-y-4">
            <MiniScheduleChart title="Live (Before)" counts={liveBeforeCounts} barMax={unifiedBarMax} />
            <MiniScheduleChart title="Draft" counts={draftCounts} colorizeRange barMax={unifiedBarMax} />
            <MiniScheduleChart title="Live (After)" counts={liveAfterCounts} colorizeRange barMax={unifiedBarMax} />
        </div>
    );

    return (!isOpen ? null : (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-7xl max-h-[95vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900">Publish Draft to Live</h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Draft: <span className="font-medium">{currentDraftName}</span> • Year: {selectedYearForView}
                            </p>
                        </div>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600" title="Close" aria-label="Close">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Two Column Layout */}
                <div className="flex-1 flex min-h-0">
                    {/* Left: Controls */}
                    <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
                        <div className="px-6 py-4 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Publishing Scope</label>
                                <div className="space-y-2">
                                    <label className="flex items-start p-2 border rounded cursor-pointer hover:bg-gray-50">
                                        <input type="radio" name="publishMode" value="all" checked={publishMode === 'all'} onChange={() => setPublishMode('all')} className="mr-2 mt-0.5" />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-gray-900">Entire Draft</div>
                                            <div className="text-xs text-gray-500">All entries for this year</div>
                                        </div>
                                    </label>

                                    <label className="flex items-start p-2 border rounded cursor-pointer hover:bg-gray-50">
                                        <input type="radio" name="publishMode" value="period" checked={publishMode === 'period'} onChange={() => setPublishMode('period')} className="mr-2 mt-0.5" />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-gray-900">Specific Period</div>
                                            <div className="text-xs text-gray-500 mb-2">Entries from a selected period</div>
                                            {publishMode === 'period' && (
                                                <select value={selectedPeriodId || ''} onChange={(e) => setSelectedPeriodId(e.target.value)} className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" onClick={(e) => e.stopPropagation()}>
                                                    <option value="">Select period...</option>
                                                    {timePeriods.map(period => (
                                                        <option key={period.id} value={period.id}>{period.name} (W{period.start_week}-{period.end_week})</option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                    </label>

                                    <label className="flex items-start p-2 border rounded cursor-pointer hover:bg-gray-50">
                                        <input type="radio" name="publishMode" value="custom" checked={publishMode === 'custom'} onChange={() => setPublishMode('custom')} className="mr-2 mt-0.5" />
                                        <div className="flex-1">
                                            <div className="text-sm font-medium text-gray-900">Custom Week Range</div>
                                            <div className="text-xs text-gray-500 mb-2">Enter week numbers (1-53)</div>
                                            {publishMode === 'custom' && (
                                                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                                    <input type="number" min="1" max="53" value={customStartWeek} onChange={(e) => setCustomStartWeek(e.target.value)} placeholder="Start" className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                                    <span className="text-gray-500 text-xs">to</span>
                                                    <input type="number" min="1" max="53" value={customEndWeek} onChange={(e) => setCustomEndWeek(e.target.value)} placeholder="End" className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                                </div>
                                            )}
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {warnings.length > 0 && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                                    <div className="flex items-start">
                                        <svg className="w-4 h-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <div className="flex-1">
                                            <h4 className="text-xs font-medium text-yellow-800 mb-1">{warnings.length} Warning{warnings.length > 1 ? 's' : ''}</h4>
                                            <ul className="text-xs text-yellow-700 space-y-0.5">
                                                {warnings.slice(0, 3).map((warning, index) => (
                                                    <li key={index} className="flex items-start"><span className="mr-1">•</span><span className="line-clamp-2">{warning}</span></li>
                                                ))}
                                                {warnings.length > 3 && <li className="text-yellow-600 italic text-xs">+{warnings.length - 3} more...</li>}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="bg-blue-50 border border-blue-200 rounded p-3">
                                <h4 className="text-xs font-medium text-blue-900 mb-1.5">Publishing Process</h4>
                                <ul className="text-xs text-blue-800 space-y-0.5">
                                    <li>• Draft saved automatically</li>
                                    <li>• Live backup created</li>
                                    <li>• {effectiveWeekRange ? `Publishes weeks ${effectiveWeekRange.startWeek}-${effectiveWeekRange.endWeek}` : 'Publishes all entries'}</li>
                                    <li>• Stay in draft mode after</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Right: Visualization */}
                    <div className="w-2/3 flex flex-col">
                        <div className="flex border-b border-gray-200 flex-shrink-0">
                            <button onClick={() => setActiveTab('stats')} className={`px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'stats' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Overview</button>
                            <button onClick={() => setActiveTab('added')} className={`px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'added' ? 'border-green-600 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Added ({stats.addedCount})</button>
                            <button onClick={() => setActiveTab('removed')} className={`px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'removed' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Removed ({stats.removedCount})</button>
                            <button onClick={() => setActiveTab('modified')} className={`px-4 py-3 text-sm font-medium border-b-2 ${activeTab === 'modified' ? 'border-orange-600 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Modified ({stats.modifiedCount})</button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {isLoadingPreview ? (
                                <div className="flex items-center justify-center h-64">
                                    <div className="text-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                        <p className="text-sm text-gray-600">Calculating changes...</p>
                                    </div>
                                </div>
                            ) : activeTab === 'stats' && (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-semibold text-gray-900">Change Summary</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <div className="text-sm text-gray-600 mb-1">Live Schedule (Before)</div>
                                            <div className="text-3xl font-bold text-gray-900">{stats.liveBeforeCount}</div>
                                            <div className="text-xs text-gray-500 mt-1">entries</div>
                                        </div>
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <div className="text-sm text-gray-600 mb-1">Live Schedule (After)</div>
                                            <div className="text-3xl font-bold text-gray-900">{stats.liveAfterCount}</div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                {stats.liveAfterCount > stats.liveBeforeCount ? <span className="text-green-600">+{stats.liveAfterCount - stats.liveBeforeCount}</span> : stats.liveAfterCount < stats.liveBeforeCount ? <span className="text-red-600">{stats.liveAfterCount - stats.liveBeforeCount}</span> : <span>no change</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <ChartsBlock />
                                    {effectiveWeekRange && (
                                        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                                            <p className="text-sm text-blue-900"><strong>Publishing Range:</strong> Weeks {effectiveWeekRange.startWeek}-{effectiveWeekRange.endWeek}</p>
                                            <p className="text-xs text-blue-700 mt-1">Only courses starting in this range will be affected. Other Live entries remain unchanged.</p>
                                        </div>
                                    )}
                                    {previewData && (
                                        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
                                            <p className="text-sm text-green-900"><strong>Server Preview:</strong> This preview is generated by the server using the exact same logic that will be used during publishing.</p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {!isLoadingPreview && activeTab === 'added' && (
                                <div className="space-y-2">
                                    <h3 className="text-lg font-semibold text-green-900 mb-3">Schedule Overview</h3>
                                    <ChartsBlock />
                                </div>
                            )}

                            {!isLoadingPreview && activeTab === 'removed' && (
                                <div className="space-y-2">
                                    <h3 className="text-lg font-semibold text-red-900 mb-3">Schedule Overview</h3>
                                    <ChartsBlock />
                                </div>
                            )}

                            {!isLoadingPreview && activeTab === 'modified' && (
                                <div className="space-y-2">
                                    <h3 className="text-lg font-semibold text-orange-900 mb-3">Schedule Overview</h3>
                                    <ChartsBlock />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center flex-shrink-0">
                    <div className="text-sm text-gray-600">{stats.addedCount + stats.removedCount + stats.modifiedCount} total changes</div>
                    <div className="flex gap-3">
                        <Button onClick={onClose} variant="secondary" disabled={isPublishing} title="Cancel" aria-label="Cancel">Cancel</Button>
                        <Button onClick={handlePublish} variant="primary" disabled={!isValidSelection() || isPublishing || isLoadingPreview} className="bg-green-600 hover:bg-green-700" title={isPublishing ? 'Publishing...' : isLoadingPreview ? 'Calculating changes...' : 'Publish to Live'} aria-label={isPublishing ? 'Publishing' : isLoadingPreview ? 'Calculating changes' : 'Publish to Live'}>
                            {isPublishing ? 'Publishing...' : isLoadingPreview ? 'Calculating...' : 'Publish to Live'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    ));
};

export default PublishDraftModal;

